import { useRoute } from '@js/utils/route';
import { toastError, toastSuccess, toastWarning } from '@js/utils/toast';
import { ref } from 'vue';

/**
 * Trailing punctuation is left out of the match: an address written at the end of a sentence
 * ends on a full stop that belongs to the text, not to the link.
 */
const URL_PATTERN = /https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]}]/gi;

export const useShortLink = () => {
    const { route } = useRoute();

    const isShortening = ref(false);

    function extractUrls(text) {
        return [...new Set(text.match(URL_PATTERN) ?? [])];
    }

    /**
     * @returns {Promise<?Object>} the short address of each URL, keyed by the original. A URL
     *                             that gains nothing from being shortened is simply absent, and
     *                             a failed call answers null — it has already been reported.
     */
    async function shortenUrls(urls) {
        isShortening.value = true;

        try {
            const response = await axios.post(
                route('front.shortLink.postShortLinks'),
                { urls },
            );

            return response.data.links ?? {};
        } catch (error) {
            toastError(error, "Les liens n'ont pas pu être raccourcis.");

            return null;
        } finally {
            isShortening.value = false;
        }
    }

    /**
     * Replaces every address of the given fields with its shortened form. The fields are handled
     * in one go so that a single call covers the whole post, and each one is only written back
     * when it actually changed.
     *
     * @param {Array<{ getValue: Function, setValue: Function }>} fields
     */
    async function shortenFieldsUrls(fields) {
        const values = fields.map((field) => field.getValue() ?? '');

        const urls = extractUrls(values.join('\n'));

        if (urls.length === 0) {
            toastWarning('Aucun lien à raccourcir.');

            return;
        }

        const links = await shortenUrls(urls);

        if (links === null) {
            return;
        }

        const count = Object.keys(links).length;

        if (count === 0) {
            toastWarning('Ces liens sont déjà aussi courts que possible.');

            return;
        }

        fields.forEach((field, index) => {
            let value = values[index];

            Object.entries(links).forEach(([url, shortUrl]) => {
                value = value.split(url).join(shortUrl);
            });

            if (value !== values[index]) {
                field.setValue(value);
            }
        });

        toastSuccess(
            count > 1 ? `${count} liens raccourcis.` : 'Lien raccourci.',
        );
    }

    return { isShortening, extractUrls, shortenUrls, shortenFieldsUrls };
};
